import React, { useState, useEffect } from 'react';
import { workflowApi, usersApi } from '../api/api';
import './WorkflowManagement.css';

function WorkflowManagement() {
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStep, setEditingStep] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    stepName: '',
    description: '',
    responsiblePerson: '',
    required: 'Yes',
  });

  useEffect(() => {
    fetchWorkflow();
    fetchUsers();
  }, []);

  const fetchWorkflow = async () => {
    try {
      const response = await workflowApi.getAll();
      setWorkflowSteps(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching workflow:', error);
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAll({ active: 'true', limit: 1000 });
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const startEditing = (step) => {
    setEditingStep(step._id);
    setFormData({
      stepName: step.stepName,
      description: step.description,
      responsiblePerson: step.responsiblePerson,
      required: step.required,
    });
  };

  const cancelEditing = () => {
    setEditingStep(null);
    setFormData({
      stepName: '',
      description: '',
      responsiblePerson: '',
      required: 'Yes',
    });
  };

  const saveStep = async () => {
    try {
      await workflowApi.update(editingStep, formData);
      await fetchWorkflow();
      cancelEditing();
    } catch (error) {
      console.error('Error updating step:', error);
      alert('Failed to update step');
    }
  };

  const deleteStep = async (id) => {
    if (window.confirm('Are you sure you want to delete this workflow step? This will affect all clients.')) {
      try {
        await workflowApi.delete(id);
        await fetchWorkflow();
      } catch (error) {
        console.error('Error deleting step:', error);
        alert('Failed to delete step');
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      stepName: '',
      description: '',
      responsiblePerson: '',
      required: 'Yes',
    });
    setShowAddModal(true);
  };

  const addStep = async (e) => {
    e.preventDefault();
    try {
      await workflowApi.create(formData);
      await fetchWorkflow();
      setShowAddModal(false);
      setFormData({
        stepName: '',
        description: '',
        responsiblePerson: '',
        required: 'Yes',
      });
    } catch (error) {
      console.error('Error adding step:', error);
      alert('Failed to add step');
    }
  };

  const moveStep = async (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === workflowSteps.length - 1)
    ) {
      return;
    }

    const newSteps = [...workflowSteps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];

    try {
      console.log(`Moving step ${direction}, new order:`, newSteps.map(s => s.stepName));
      const response = await workflowApi.reorder(newSteps);
      console.log('Reorder response:', response.data);
      setWorkflowSteps(response.data); // Use response data instead of fetching again
    } catch (error) {
      console.error('Error reordering steps:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to reorder steps: ${error.response?.data?.message || error.message}`);
      await fetchWorkflow(); // Fetch to restore correct order
    }
  };

  const syncToAllClients = async () => {
    if (
      window.confirm(
        'This will update all existing clients to match the current workflow template.\n\n' +
          '- New steps will be added to all clients\n' +
          '- Step details (name, description, responsible person) will be updated\n' +
          '- Completion status will be preserved\n\n' +
          'Do you want to proceed?'
      )
    ) {
      try {
        const response = await workflowApi.sync();
        alert(response.data.message);
        await fetchWorkflow();
      } catch (error) {
        console.error('Error syncing workflow:', error);
        alert('Failed to sync workflow to clients');
      }
    }
  };

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  return (
    <div className="workflow-management">
      <div className="card">
        <div className="header-row">
          <div>
            <h2>Workflow Management</h2>
            <p className="description">
              This is the standard workflow template for all client integrations. Each client's
              progress is tracked against these steps.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-primary" onClick={syncToAllClients}>
              Sync to All Clients
            </button>
            <button className="btn btn-success" onClick={openAddModal}>
              Add Step
            </button>
          </div>
        </div>

        <div className="workflow-steps-list">
          {workflowSteps.map((step, index) => (
            <div
              key={step._id}
              className={`workflow-step-item ${editingStep === step._id ? 'editing' : ''}`}
            >
              <div className="step-number">{index + 1}</div>

              {editingStep === step._id ? (
                <div className="step-edit-form">
                  <div className="form-group">
                    <label>Step Name</label>
                    <input
                      type="text"
                      name="stepName"
                      value={formData.stepName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Responsible Person</label>
                    <select
                      name="responsiblePerson"
                      value={formData.responsiblePerson}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a user...</option>
                      {users && users.map((user) => (
                        <option key={user._id} value={user.displayName}>
                          {user.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Required</label>
                    <select name="required" value={formData.required} onChange={handleInputChange}>
                      <option value="Yes">Required</option>
                      <option value="Optional">Optional</option>
                    </select>
                  </div>
                  <div className="step-actions">
                    <button className="btn btn-success btn-sm" onClick={saveStep}>
                      Save
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={cancelEditing}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="step-content">
                    <div className="step-title-row">
                      <h3>{step.stepName}</h3>
                      {step.required === 'Optional' ? (
                        <span className="badge badge-optional">Optional</span>
                      ) : (
                        <span className="badge badge-required">Required</span>
                      )}
                    </div>
                    <p className="step-desc">{step.description}</p>
                    <p className="step-resp">
                      <strong>Responsible:</strong> {step.responsiblePerson}
                    </p>
                  </div>
                  <div className="step-controls">
                    <div className="reorder-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => moveStep(index, 'up')}
                        disabled={index === 0}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => moveStep(index, 'down')}
                        disabled={index === workflowSteps.length - 1}
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => startEditing(step)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteStep(step._id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="workflow-hint">
          <strong>Tip:</strong> You can reorder steps with the arrow buttons.
        </div>
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Workflow Step</h2>
            <form onSubmit={addStep}>
              <div className="form-group">
                <label>Step Name *</label>
                <input
                  type="text"
                  name="stepName"
                  value={formData.stepName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>
              <div className="form-group">
                <label>Responsible Person *</label>
                <select
                  name="responsiblePerson"
                  value={formData.responsiblePerson}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a user...</option>
                  {users && users.map((user) => (
                    <option key={user._id} value={user.displayName}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Required</label>
                <select name="required" value={formData.required} onChange={handleInputChange}>
                  <option value="Yes">Required</option>
                  <option value="Optional">Optional</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-success">
                  Add Step
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkflowManagement;
